import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
    canPickCourse,
    parseSubjectKey,
    toQueryParams,
    type ReportFilters,
    type ReportScope,
    type StatementFormat,
} from "../domain/PractitionerReport";
import { ReportsAPI } from "../infrastructure/reports.api";

export const PRACTITIONER_REPORT_KEY = "practitioner-report";

const scopeKey = (scope: ReportScope): string[] => {
    switch (scope.kind) {
        case "self":
            return ["self"];
        case "member":
            return ["member", scope.centerId, scope.therapistId];
        case "centre":
            return ["centre", scope.centerId];
        case "course":
            return ["course", scope.centerId, scope.courseId];
    }
};

/** A 422 the person can act on: the window holds more bars than one chart can draw. */
const isTooManyPeriods = (error: unknown) =>
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    error.response?.data?.detail === "too_many_periods";

/**
 * The practitioner's report for the filters. `tooManyPeriods` is told apart
 * from any other failure, which only offers a retry.
 */
export function usePractitionerReport(scope: ReportScope, filters: ReportFilters) {
    const params = toQueryParams(filters);
    const query = useQuery({
        queryKey: [PRACTITIONER_REPORT_KEY, ...scopeKey(scope), params],
        queryFn: async () => (await ReportsAPI.report(scope, params)).data,
        retry: false,
    });
    const tooManyPeriods = isTooManyPeriods(query.error);
    return {
        report: query.data,
        loading: query.isLoading,
        tooManyPeriods,
        error: query.isError && !tooManyPeriods,
        retry: () => void query.refetch(),
    };
}

/**
 * Who the sessions in the window were with, for the picker. `enabled` false
 * when the page is already about one of them.
 */
export function usePractitionerSubjects(scope: ReportScope, from: string, to: string, enabled = true) {
    const params = { start: `${from}T00:00:00`, end: `${to}T23:59:59` };
    const query = useQuery({
        queryKey: [PRACTITIONER_REPORT_KEY, "subjects", ...scopeKey(scope), params],
        queryFn: async () => (await ReportsAPI.subjects(scope, params)).data,
        enabled: enabled && scope.kind !== "centre",
        retry: false,
    });
    return { subjects: query.data ?? [], loading: query.isLoading };
}

/**
 * The courses of the scope's sessions in the window, for the pickers — only
 * one student's when `subject` names them. Empty outside training centres.
 */
export function usePractitionerCourses(scope: ReportScope, from: string, to: string, subject = "") {
    const params = {
        start: `${from}T00:00:00`,
        end: `${to}T23:59:59`,
        ...(parseSubjectKey(subject) ?? {}),
    };
    const query = useQuery({
        queryKey: [PRACTITIONER_REPORT_KEY, "courses", ...scopeKey(scope), params],
        queryFn: async () => (await ReportsAPI.courses(scope, params)).data,
        enabled: canPickCourse(scope),
        retry: false,
    });
    return { courses: query.data ?? [] };
}

/** A training centre's courses over the window, across their mentors. */
export function useCourseOverview(centerId: string | undefined, from: string, to: string) {
    const params = { start: `${from}T00:00:00`, end: `${to}T23:59:59` };
    const query = useQuery({
        queryKey: [PRACTITIONER_REPORT_KEY, "course-overview", centerId, params],
        queryFn: async () => (await ReportsAPI.courseOverview(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });
    return {
        overview: query.data,
        loading: query.isLoading,
        error: query.isError,
        retry: () => void query.refetch(),
    };
}

function saveBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

export type StatementPeriod = "month" | "year";

export interface StatementRequest {
    period: StatementPeriod;
    /** `YYYY-MM` for a month, `YYYY` for a year. */
    value: string;
    subject: string;
    /** A course id, or empty. */
    course?: string;
    /** One professional of a report across several, or empty. */
    therapist?: string;
    locale: string;
    format?: StatementFormat;
}

/** Download a month's or a year's statement as the PDF the backend draws. */
export function useStatementDownload(scope: ReportScope) {
    const mutation = useMutation({
        mutationFn: async (input: StatementRequest) => {
            const subject = parseSubjectKey(input.subject) ?? {};
            const format = input.format ?? "pdf";
            const { data } = await ReportsAPI.statement(
                scope,
                {
                    [input.period]: input.value,
                    locale: input.locale.startsWith("en") ? "en" : "es",
                    ...subject,
                    ...(input.course ? { course_id: input.course } : {}),
                    ...(input.therapist ? { therapist_id: input.therapist } : {}),
                },
                format
            );
            saveBlob(data, `activity-${input.value}.${format}`);
        },
    });
    return {
        download: mutation.mutateAsync,
        downloading: mutation.isPending,
        error: mutation.isError,
        reset: mutation.reset,
    };
}
